import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Plus } from "lucide-react";
import { api, apiError, eur } from "@/lib/api";
import { useI18n } from "@/i18n/LanguageContext";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";

export default function MyBookings() {
  const { t } = useI18n();
  const [bookings, setBookings] = useState(null);

  const load = () =>
    api
      .get("/bookings/mine")
      .then(({ data }) => setBookings(data.bookings || []))
      .catch((e) => toast.error(apiError(e)));

  useEffect(() => {
    load();
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="mx-auto max-w-6xl px-5 py-12 lg:px-10 lg:py-16">
        <div className="flex items-end justify-between gap-4">
          <h1 className="font-display text-3xl tracking-tight sm:text-4xl" data-testid="bookings-title">
            {t("bookings.title")}
          </h1>
          <Link to="/">
            <Button data-testid="new-booking-btn" className="bg-sea hover:bg-sea-dark">
              <Plus size={16} className="mr-1.5" /> {t("bookings.bookNow")}
            </Button>
          </Link>
        </div>

        {bookings === null && <p className="mt-14 text-slate-500">{t("common.loading")}</p>}
        {bookings?.length === 0 && (
          <p className="mt-14 text-slate-500" data-testid="bookings-empty">
            {t("bookings.empty")}
          </p>
        )}

        <div className="mt-10 space-y-4" data-testid="bookings-list">
          {(bookings || []).map((b, i) => (
            <Link
              key={b.booking_id}
              to={`/bookings/${b.booking_id}`}
              data-testid={`booking-card-${b.code}`}
              className="block rounded-xl border border-slate-200 bg-white p-6 transition-transform duration-300 hover:-translate-y-0.5 hover:border-sea ambient fade-up"
              style={{ animationDelay: `${i * 50}ms` }}
            >
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <p className="label-eyebrow">
                    {t("bookings.code")} {b.code}
                  </p>
                  <p className="mt-2 text-base font-medium text-slate-900">
                    {b.origin?.name} <ArrowRight size={13} className="inline text-slate-400" />{" "}
                    {b.destination?.name}
                  </p>
                  <p className="mt-1.5 text-sm text-slate-500">
                    {b.pickup_at?.replace("T", " · ").slice(0, 18)} · {b.vehicle_class_name} · {b.pax}{" "}
                    {t("results.pax")}
                  </p>
                </div>
                <div className="text-right">
                  <StatusBadge status={b.status} testId={`booking-status-${b.code}`} />
                  <p className="mt-3 font-display text-2xl text-sea">{eur(b.price_eur)}</p>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
      <Footer />
    </div>
  );
}
